import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { decodeToken } from 'react-jwt';
import {
  FaFolderOpen, FaPlus, FaSearch, FaTimes, FaUpload, FaDownload, FaTrash, FaEdit, FaFileSignature,
  FaCompressAlt, FaArrowLeft, FaChevronLeft, FaChevronRight, FaCar, FaUser, FaClipboardList, FaCheckCircle,
  FaCalendarDay, FaHistory,
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';
import { buscarVeiculoDoDocumento } from '../others/cadastroSync';
import { buscarClientes } from '../others/buscaCliente';
import { otimizarArquivo } from '../others/otimizarArquivo';
import {
  TAMANHOS_DE_PAGINA, LIMITE_COMUM_MB, LIMITE_ASSINADO_MB, formatarTamanho, temAssinaturaDigital,
  classificarArquivo, parametrosDaLista, faixaDaPagina, formatarDocumento, formatarData, palavrasDoNome,
  resumoDaPagina, dataLocal, somarDias, diaDoFiltro,
} from '../others/processos';
import {
  PageBackground, Content, TitleRow, Title, Card, HintText, EmptyState, Input, PrimaryButton, SecondaryButton,
  ModalOverlay, ModalBox, ModalHeader, ModalClose, ModalContent,
} from './Procuracao';

// Processos do despachante: o registro (veículo, cliente, serviços) e os documentos escaneados, guardados para
// consulta. Abre por aqui (digitado ou importando da OS), pelo modal depois de gerar a OS, e pelo histórico no
// cadastro do cliente/veículo (/processos?abrir={id}).
//
// GET    /processos?inicio&fim&placa&cliente&status&pagina&tamanho   -> { itens, total, pagina, tamanho }
// GET    /processos/{id}                                            -> { processo, arquivos }
// GET    /processos/ordens-disponiveis                              -> OS com veículo ainda sem processo
// POST   /processos/da-ordem/{idOrdem}  [idsOrdemVeiculo]           -> um processo por veículo
// POST   /processos | PUT /processos/{id} | DELETE /processos/{id} (só gerente)
// POST   /processos/{id}/arquivos  (file, assinado)  | DELETE /processos/{id}/arquivos/{idArquivo} (só gerente)
// GET    /arquivos/download/{idArquivo}

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
const FILTROS_VAZIOS = { inicio: '', fim: '', placa: '', cliente: '', status: '' };

const ehGestor = () => {
  const dados = decodeToken(localStorage.getItem('jwtToken') || '') || {};
  return dados.tipoUsuario === 'GERENTE' || dados.tipoUsuario === 'MANAGER';
};

const mensagemDeErro = (error, padrao) => {
  const data = error?.response?.data;
  return (data && (data.message || (typeof data === 'string' ? data : null))) || padrao;
};

function Processos() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_VAZIOS);
  const [pagina, setPagina] = useState(0);
  const [tamanho, setTamanho] = useState(10);
  const [dados, setDados] = useState({ itens: [], total: 0 });
  const [carregando, setCarregando] = useState(false);
  const [recarga, setRecarga] = useState(0);

  const [novoAberto, setNovoAberto] = useState(false);
  const [idDetalhe, setIdDetalhe] = useState(null);

  useEffect(() => {
    const abrir = new URLSearchParams(location.search).get('abrir');
    if (abrir) setIdDetalhe(Number(abrir));
  }, [location.search]);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setCarregando(true);
      try {
        const { data } = await axios.get(`${API_BASE_URL}/processos`,
          { ...auth(), params: parametrosDaLista(filtrosAplicados, pagina, tamanho) });
        if (!cancelado) setDados(data);
      } catch (error) {
        if (!cancelado) addToast('error', mensagemDeErro(error, 'Erro ao carregar os processos'));
      } finally {
        if (!cancelado) setCarregando(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAplicados, pagina, tamanho, recarga]);

  const aplicarFiltros = (e) => {
    e.preventDefault();
    setPagina(0);
    setFiltrosAplicados(filtros);
  };

  const limparFiltros = () => {
    setFiltros(FILTROS_VAZIOS);
    setFiltrosAplicados(FILTROS_VAZIOS);
    setPagina(0);
  };

  // Atalhos de exibição: um dia (com ◀ ▶) ou os mais recentes. Também preenchem os filtros acima.
  const verDia = (dia) => {
    const f = { ...FILTROS_VAZIOS, inicio: dia, fim: dia };
    setFiltros(f);
    setFiltrosAplicados(f);
    setPagina(0);
  };

  const verUltimos = () => {
    setFiltros(FILTROS_VAZIOS);
    setFiltrosAplicados(FILTROS_VAZIOS);
    setTamanho(10);
    setPagina(0);
  };

  const hoje = dataLocal();
  const diaExibido = diaDoFiltro(filtrosAplicados);
  const semFiltro = Object.values(filtrosAplicados).every((v) => !String(v || '').trim());
  const contador = resumoDaPagina(dados.itens);

  const recarregar = () => setRecarga((r) => r + 1);
  const totalPaginas = Math.max(1, Math.ceil((dados.total || 0) / tamanho));

  const fecharDetalhe = () => {
    setIdDetalhe(null);
    if (location.search) navigate('/processos', { replace: true });
  };

  return (
    <PageBackground>
      <Header />
      <Content style={{ maxWidth: 1150 }}>
        <TitleRow>
          <Title><FaFolderOpen size={22} /> Processos</Title>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SecondaryButton type="button" onClick={() => navigate('/home')}><FaArrowLeft /> Voltar</SecondaryButton>
            <PrimaryButton type="button" onClick={() => setNovoAberto(true)}><FaPlus /> Registrar novo processo</PrimaryButton>
          </div>
        </TitleRow>

        <Card>
          <FiltrosForm onSubmit={aplicarFiltros}>
            <label>
              <span>De</span>
              <Input type="date" value={filtros.inicio} onChange={(e) => setFiltros({ ...filtros, inicio: e.target.value })} />
            </label>
            <label>
              <span>Até</span>
              <Input type="date" value={filtros.fim} onChange={(e) => setFiltros({ ...filtros, fim: e.target.value })} />
            </label>
            <label>
              <span>Placa ou chassi</span>
              <Input value={filtros.placa} onChange={(e) => setFiltros({ ...filtros, placa: e.target.value })} placeholder="ABC1D23" />
            </label>
            <label className="largo">
              <span>Cliente (nome ou CPF/CNPJ)</span>
              <Input value={filtros.cliente} onChange={(e) => setFiltros({ ...filtros, cliente: e.target.value })} placeholder="Nome ou documento" />
            </label>
            <label>
              <span>Status</span>
              <Select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}>
                <option value="">Todos</option>
                <option value="PENDENTE">Pendente de upload</option>
                <option value="ENVIADO">Com arquivos</option>
              </Select>
            </label>
            <div className="botoes">
              <PrimaryButton type="submit"><FaSearch /> Filtrar</PrimaryButton>
              <SecondaryButton type="button" onClick={limparFiltros}>Limpar</SecondaryButton>
            </div>
          </FiltrosForm>

          {/* Exibição rápida (por dia ou os mais recentes) e o contador da página, como na tela de OS */}
          <BarraExibicao>
            <div className="atalhos">
              <Atalho type="button" className={diaExibido === hoje ? 'ativo' : ''} onClick={() => verDia(hoje)}>
                <FaCalendarDay /> Hoje
              </Atalho>
              <Atalho type="button" className={semFiltro && tamanho === 10 ? 'ativo' : ''} onClick={verUltimos}>
                <FaHistory /> Últimos 10
              </Atalho>
              {diaExibido ? (
                <span className="dia">
                  <BotaoPagina type="button" onClick={() => verDia(somarDias(diaExibido, -1))} aria-label="Dia anterior"><FaChevronLeft /></BotaoPagina>
                  <strong>{diaExibido === hoje ? 'Hoje, ' : ''}{formatarData(diaExibido)}</strong>
                  <BotaoPagina type="button" onClick={() => verDia(somarDias(diaExibido, 1))} aria-label="Próximo dia"><FaChevronRight /></BotaoPagina>
                </span>
              ) : (
                <span className="legenda">{semFiltro ? 'Mais recentes primeiro' : 'Filtro aplicado'}</span>
              )}
            </div>

            <div className="contadores">
              <Contador>
                <strong>{carregando ? '—' : contador.total}</strong>
                <span>Na página{!carregando && dados.total > contador.total ? ` (de ${dados.total})` : ''}</span>
              </Contador>
              <Contador $cor="#27ae60">
                <strong>{carregando ? '—' : contador.enviados}</strong>
                <span>Com upload</span>
              </Contador>
              <Contador $cor="#e74c3c">
                <strong>{carregando ? '—' : contador.pendentes}</strong>
                <span>Pendentes</span>
              </Contador>
            </div>
          </BarraExibicao>
        </Card>

        <Card>
          {carregando ? (
            <EmptyState>Carregando...</EmptyState>
          ) : dados.itens.length === 0 ? (
            <EmptyState>Nenhum processo encontrado.</EmptyState>
          ) : (
            <Lista>
              {dados.itens.map((p) => (
                <LinhaProcesso key={p.idProcesso} type="button" onClick={() => setIdDetalhe(p.idProcesso)}>
                  <Bolinha $enviado={p.status === 'ENVIADO'}
                    title={p.status === 'ENVIADO' ? `${p.quantidadeArquivos} arquivo(s) enviado(s)` : 'Pendente de upload'} />
                  <div className="data">{formatarData(p.dataProcesso)}</div>
                  <div className="veiculo">
                    <strong>{p.placa || p.chassi}</strong>
                    <span>{[p.marcaModelo, p.placa && p.chassi ? p.chassi : null].filter(Boolean).join(' · ')}</span>
                  </div>
                  <div className="cliente">
                    <strong>{p.nomeCliente || '—'}</strong>
                    <span>{formatarDocumento(p.cpfCnpjCliente)}</span>
                  </div>
                  <div className="servicos">{p.servicos || <em>Sem serviço informado</em>}</div>
                  <div className="extra">
                    {p.numeroOrdem && <span className="os">OS {p.numeroOrdem}</span>}
                    <span>{p.quantidadeArquivos} arq.</span>
                  </div>
                </LinhaProcesso>
              ))}
            </Lista>
          )}

          <Paginacao>
            <label>
              Mostrar
              <Select value={tamanho} onChange={(e) => { setTamanho(Number(e.target.value)); setPagina(0); }}>
                {TAMANHOS_DE_PAGINA.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
              por página
            </label>
            <span>{faixaDaPagina(pagina, tamanho, dados.total)}</span>
            <div>
              <BotaoPagina type="button" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)} aria-label="Página anterior"><FaChevronLeft /></BotaoPagina>
              <BotaoPagina type="button" disabled={pagina + 1 >= totalPaginas} onClick={() => setPagina(pagina + 1)} aria-label="Próxima página"><FaChevronRight /></BotaoPagina>
            </div>
          </Paginacao>
        </Card>
      </Content>

      {novoAberto && (
        <ModalNovoProcesso
          onFechar={() => setNovoAberto(false)}
          onCriados={(processos) => {
            setNovoAberto(false);
            recarregar();
            if (processos.length) setIdDetalhe(processos[0].idProcesso);
            if (processos.length > 1) addToast('success', `${processos.length} processos abertos (um por veículo).`);
          }}
        />
      )}

      {idDetalhe && (
        <DetalheProcesso
          idProcesso={idDetalhe}
          onFechar={fecharDetalhe}
          onAlterado={recarregar}
          onExcluido={() => { fecharDetalhe(); recarregar(); }}
        />
      )}
    </PageBackground>
  );
}

// ══════════════════════════════════════════════════════════════
// NOVO PROCESSO: importar da OS ou digitar
// ══════════════════════════════════════════════════════════════
function ModalNovoProcesso({ onFechar, onCriados }) {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  // Digitar é o caminho principal; as OS disponíveis carregam em segundo plano para a outra aba
  const [aba, setAba] = useState('manual');
  const [ordens, setOrdens] = useState(null);
  const [escolhidos, setEscolhidos] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/processos/ordens-disponiveis`, auth())
      .then(({ data }) => setOrdens(data || []))
      .catch(() => setOrdens([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alternar = (idOrdem, idOrdemVeiculo) => setEscolhidos((atual) => {
    const lista = atual[idOrdem] || [];
    const nova = lista.includes(idOrdemVeiculo) ? lista.filter((i) => i !== idOrdemVeiculo) : [...lista, idOrdemVeiculo];
    return { ...atual, [idOrdem]: nova };
  });

  const abrirDasOrdens = async () => {
    const pedidos = Object.entries(escolhidos).filter(([, ids]) => ids.length);
    if (!pedidos.length) {
      addToast('warning', 'Marque pelo menos um veículo.');
      return;
    }
    setSalvando(true);
    try {
      const criados = [];
      for (const [idOrdem, ids] of pedidos) {
        const { data } = await axios.post(`${API_BASE_URL}/processos/da-ordem/${idOrdem}`, ids, auth());
        criados.push(...data);
      }
      onCriados(criados);
    } catch (error) {
      addToast('error', mensagemDeErro(error, 'Não foi possível abrir o processo'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalBox style={{ maxWidth: 760 }}>
        <ModalHeader>
          <h3>Registrar novo processo</h3>
          <ModalClose onClick={onFechar}><FaTimes /></ModalClose>
        </ModalHeader>
        <ModalContent>
          <Abas>
            <button type="button" className={aba === 'manual' ? 'ativa' : ''} onClick={() => setAba('manual')}><FaEdit /> Digitar</button>
            <button type="button" className={aba === 'os' ? 'ativa' : ''} onClick={() => setAba('os')}>
              <FaClipboardList /> Importar da OS{ordens?.length ? ` (${ordens.length})` : ''}
            </button>
          </Abas>

          {aba === 'os' ? (
            <>
              <HintText style={{ marginBottom: 10 }}>
                Ordens de serviço recentes com veículo ainda sem processo. Cada veículo marcado vira um processo, já com
                cliente, veículo e serviços preenchidos.
              </HintText>
              {ordens === null ? (
                <EmptyState>Carregando...</EmptyState>
              ) : ordens.length === 0 ? (
                <EmptyState>Nenhuma OS com veículo pendente de processo. Use a aba "Digitar".</EmptyState>
              ) : (
                <ListaOrdens>
                  {ordens.map((os) => (
                    <div key={os.idOrdemServico} className="ordem">
                      <div className="cabecalho">
                        <strong>OS {os.numeroOrdem}</strong>
                        <span>{os.nomeCliente} · {os.data ? new Date(os.data).toLocaleDateString('pt-BR') : ''}</span>
                      </div>
                      {os.veiculos.map((v) => (
                        <label key={v.idOrdemVeiculo} className={v.idProcesso ? 'feito' : ''}>
                          <input
                            type="checkbox"
                            disabled={!!v.idProcesso}
                            checked={!!v.idProcesso || (escolhidos[os.idOrdemServico] || []).includes(v.idOrdemVeiculo)}
                            onChange={() => alternar(os.idOrdemServico, v.idOrdemVeiculo)}
                          />
                          <FaCar />
                          <span><strong>{v.placa || v.chassi}</strong> {v.marcaModelo ? `· ${v.marcaModelo}` : ''}</span>
                          <em>{v.idProcesso ? 'já tem processo' : (v.servicos || '')}</em>
                        </label>
                      ))}
                    </div>
                  ))}
                </ListaOrdens>
              )}
              <Rodape>
                <SecondaryButton type="button" onClick={onFechar}>Cancelar</SecondaryButton>
                <PrimaryButton type="button" onClick={abrirDasOrdens} disabled={salvando}>
                  {salvando ? 'Abrindo...' : 'Abrir processo(s)'}
                </PrimaryButton>
              </Rodape>
            </>
          ) : (
            <FormularioProcesso
              onCancelar={onFechar}
              onSalvo={(processo) => onCriados([processo])}
            />
          )}
        </ModalContent>
      </ModalBox>
    </ModalOverlay>
  );
}

// ══════════════════════════════════════════════════════════════
// FORMULÁRIO (novo digitado ou edição)
// ══════════════════════════════════════════════════════════════
function FormularioProcesso({ processo, onCancelar, onSalvo }) {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const [form, setForm] = useState(() => ({
    dataProcesso: processo?.dataProcesso || new Date().toLocaleDateString('sv-SE'),
    placa: processo?.placa || '',
    chassi: processo?.chassi || '',
    marcaModelo: processo?.marcaModelo || '',
    idVeiculo: processo?.idVeiculo || null,
    nomeCliente: processo?.nomeCliente || '',
    cpfCnpjCliente: processo?.cpfCnpjCliente ? formatarDocumento(processo.cpfCnpjCliente) : '',
    idCliente: processo?.idCliente || null,
    servicos: processo?.servicos || '',
    observacao: processo?.observacao || '',
  }));
  const [catalogo, setCatalogo] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // Lupa do cliente: resultados da busca (null = não buscou) e os veículos do cliente escolhido
  const [clientesAchados, setClientesAchados] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [veiculosDoCliente, setVeiculosDoCliente] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/servicos`, auth())
      .then(({ data }) => setCatalogo((data || []).filter((s) => !s.naoRelacionadoVeiculo && s.ativo !== false)))
      .catch(() => setCatalogo([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cliente vinculado (na edição ou depois de escolher): traz os veículos do cadastro dele para importar
  useEffect(() => {
    if (!form.idCliente) { setVeiculosDoCliente([]); return undefined; }
    let cancelado = false;
    axios.get(`${API_BASE_URL}/clientes/${form.idCliente}`, auth())
      .then(({ data }) => { if (!cancelado) setVeiculosDoCliente(Array.isArray(data?.veiculos) ? data.veiculos : []); })
      .catch(() => { if (!cancelado) setVeiculosDoCliente([]); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.idCliente]);

  // Busca pelo nome e pelo CPF/CNPJ digitados e junta os resultados. Enter num dos campos busca só por ele
  // (assim um CPF que ficou preenchido não esconde a busca pelo nome).
  const buscarCliente = async (somente) => {
    const documento = somente === 'nome' ? '' : form.cpfCnpjCliente.replace(/\D/g, '');
    const nome = somente === 'documento' ? '' : form.nomeCliente.trim();
    const palavras = palavrasDoNome(nome);
    if (documento.length < 3 && !palavras.length) {
      addToast('warning', 'Digite ao menos 3 letras do nome ou números do CPF/CNPJ para buscar.');
      return;
    }
    setBuscandoCliente(true);
    try {
      setClientesAchados(await buscarClientes({ nome, documento }));
    } catch (error) {
      addToast('error', mensagemDeErro(error, 'Erro ao pesquisar clientes'));
    } finally {
      setBuscandoCliente(false);
    }
  };

  const escolherCliente = (c) => {
    setForm((f) => ({
      ...f,
      idCliente: c.idCliente,
      nomeCliente: c.nome || '',
      cpfCnpjCliente: c.cpfCnpj ? formatarDocumento(c.cpfCnpj) : '',
    }));
    setClientesAchados(null);
  };

  const usarVeiculo = (v) => setForm((f) => ({
    ...f,
    idVeiculo: v.idVeiculo,
    placa: (v.placa || '').toUpperCase(),
    chassi: (v.chassi || '').toUpperCase(),
    marcaModelo: v.marcaModelo || '',
  }));

  const enterBuscaCliente = (somente) => (e) => {
    if (e.key === 'Enter') { e.preventDefault(); buscarCliente(somente); }
  };

  const campo = (nome) => ({ value: form[nome], onChange: (e) => setForm({ ...form, [nome]: e.target.value }) });

  // Preenche veículo e dono a partir do cadastro (placa em qualquer padrão, ou chassi)
  const buscarNoCadastro = async () => {
    setBuscando(true);
    try {
      const v = await buscarVeiculoDoDocumento({ placa: form.placa, chassi: form.chassi });
      if (!v) {
        addToast('warning', 'Veículo não encontrado no cadastro. Os dados digitados serão usados.');
        return;
      }
      setForm((f) => ({
        ...f,
        idVeiculo: v.idVeiculo,
        placa: v.placa || f.placa,
        chassi: v.chassi || f.chassi,
        marcaModelo: v.marcaModelo || f.marcaModelo,
        idCliente: v.cliente?.idCliente || f.idCliente,
        nomeCliente: v.cliente?.nome || f.nomeCliente,
        cpfCnpjCliente: v.cliente?.cpfCnpj ? formatarDocumento(v.cliente.cpfCnpj) : f.cpfCnpjCliente,
      }));
      addToast('success', 'Dados do veículo e do proprietário preenchidos pelo cadastro.');
    } finally {
      setBuscando(false);
    }
  };

  const adicionarServico = (descricao) => {
    const atuais = form.servicos.split(',').map((s) => s.trim()).filter(Boolean);
    if (atuais.includes(descricao)) return;
    setForm({ ...form, servicos: [...atuais, descricao].join(', ') });
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.placa.trim() && !form.chassi.trim()) { addToast('warning', 'Informe a placa ou o chassi.'); return; }
    if (!form.nomeCliente.trim() && !form.cpfCnpjCliente.trim()) { addToast('warning', 'Informe o nome ou o CPF/CNPJ do cliente.'); return; }
    setSalvando(true);
    try {
      const corpo = { ...form };
      const { data } = processo
        ? await axios.put(`${API_BASE_URL}/processos/${processo.idProcesso}`, corpo, auth())
        : await axios.post(`${API_BASE_URL}/processos`, corpo, auth());
      addToast('success', processo ? 'Processo atualizado!' : 'Processo registrado! Agora envie os arquivos.');
      onSalvo(data);
    } catch (error) {
      addToast('error', mensagemDeErro(error, 'Erro ao salvar o processo'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form onSubmit={salvar}>
      <Grade>
        <label>
          <span>Data do processo</span>
          <Input type="date" {...campo('dataProcesso')} />
        </label>
        <label>
          <span>Placa</span>
          <Input {...campo('placa')} placeholder="ABC1D23" onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase(), idVeiculo: null })} />
        </label>
        <label>
          <span>Chassi</span>
          <Input {...campo('chassi')} onChange={(e) => setForm({ ...form, chassi: e.target.value.toUpperCase(), idVeiculo: null })} />
        </label>
        <div className="acao">
          <SecondaryButton type="button" onClick={buscarNoCadastro} disabled={buscando || (!form.placa.trim() && !form.chassi.trim())}>
            <FaSearch /> {buscando ? 'Buscando...' : 'Buscar no cadastro'}
          </SecondaryButton>
        </div>
        <label className="largo">
          <span>Marca/modelo</span>
          <Input {...campo('marcaModelo')} />
        </label>
        <label className="largo">
          <span>Cliente</span>
          <Input {...campo('nomeCliente')} onKeyDown={enterBuscaCliente('nome')} placeholder="Digite ou busque pela lupa"
            onChange={(e) => setForm({ ...form, nomeCliente: e.target.value, idCliente: null })} />
        </label>
        <label>
          <span>CPF/CNPJ</span>
          <Input {...campo('cpfCnpjCliente')} onKeyDown={enterBuscaCliente('documento')}
            onChange={(e) => setForm({ ...form, cpfCnpjCliente: e.target.value, idCliente: null })} />
        </label>
        <div className="acao">
          <SecondaryButton type="button" onClick={() => buscarCliente()} disabled={buscandoCliente}
            title="Buscar o cliente no cadastro pelo nome ou CPF/CNPJ digitado">
            <FaSearch /> {buscandoCliente ? 'Buscando...' : 'Buscar cliente'}
          </SecondaryButton>
        </div>
        {clientesAchados && (
          <div className="inteiro">
            {clientesAchados.length === 0 ? (
              <HintText>Nenhum cliente encontrado. Os dados digitados serão usados.</HintText>
            ) : (
              <ListaEscolha>
                {clientesAchados.map((c) => (
                  <button type="button" key={c.idCliente} onClick={() => escolherCliente(c)}>
                    <FaUser /> <strong>{c.nome}</strong> <span>{formatarDocumento(c.cpfCnpj)}</span>
                  </button>
                ))}
              </ListaEscolha>
            )}
          </div>
        )}
        {form.idCliente && veiculosDoCliente.length > 0 && (
          <div className="inteiro">
            <HintText style={{ marginBottom: 4 }}>Veículos do cadastro deste cliente (clique para usar):</HintText>
            <ListaEscolha>
              {veiculosDoCliente.map((v) => (
                <button type="button" key={v.idVeiculo} onClick={() => usarVeiculo(v)}
                  className={form.idVeiculo === v.idVeiculo ? 'escolhido' : ''}>
                  <FaCar /> <strong>{v.placa || v.chassi}</strong>
                  <span>{[v.marcaModelo, v.placa && v.chassi ? v.chassi : null].filter(Boolean).join(' · ')}</span>
                  {form.idVeiculo === v.idVeiculo && <FaCheckCircle color="#27ae60" style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
            </ListaEscolha>
          </div>
        )}
        <label className="inteiro">
          <span>Serviços</span>
          <Input {...campo('servicos')} placeholder="Ex.: TRANSFERÊNCIA, LICENCIAMENTO" />
          {catalogo.length > 0 && (
            <Chips>
              {catalogo.slice(0, 30).map((s) => (
                <button type="button" key={s.idServico} onClick={() => adicionarServico(s.descricao)}>+ {s.descricao}</button>
              ))}
            </Chips>
          )}
        </label>
        <label className="inteiro">
          <span>Observação</span>
          <Textarea rows={3} maxLength={2000} {...campo('observacao')} />
        </label>
      </Grade>
      {(form.idVeiculo || form.idCliente) && (
        <HintText style={{ marginTop: 6 }}>
          <FaCheckCircle color="#27ae60" /> Vinculado ao cadastro: aparece no histórico do {form.idVeiculo ? 'veículo' : ''}
          {form.idVeiculo && form.idCliente ? ' e do ' : ''}{form.idCliente ? 'cliente' : ''}.
        </HintText>
      )}
      <Rodape>
        <SecondaryButton type="button" onClick={onCancelar}>Cancelar</SecondaryButton>
        <PrimaryButton type="submit" disabled={salvando}>{salvando ? 'Salvando...' : (processo ? 'Salvar alterações' : 'Registrar processo')}</PrimaryButton>
      </Rodape>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════
// DETALHE: dados, arquivos e upload
// ══════════════════════════════════════════════════════════════
export function DetalheProcesso({ idProcesso, onFechar, onAlterado, onExcluido }) {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const [detalhe, setDetalhe] = useState(null);
  const [editando, setEditando] = useState(false);
  const [confirmar, setConfirmar] = useState(null); // { tipo: 'processo' | 'arquivo', arquivo? }
  const gestor = ehGestor();

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/processos/${idProcesso}`, auth());
      setDetalhe(data);
    } catch (error) {
      addToast('error', mensagemDeErro(error, 'Processo não encontrado'));
      onFechar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProcesso]);

  useEffect(() => { carregar(); }, [carregar]);

  const aposMudanca = () => { carregar(); if (onAlterado) onAlterado(); };

  const baixar = async (arquivo) => {
    try {
      const resposta = await axios.get(`${API_BASE_URL}/arquivos/download/${arquivo.id}`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(resposta.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = arquivo.nomeOriginal || 'documento';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      addToast('error', 'Não foi possível baixar o arquivo');
    }
  };

  const excluir = async () => {
    const alvo = confirmar;
    setConfirmar(null);
    try {
      if (alvo.tipo === 'processo') {
        await axios.delete(`${API_BASE_URL}/processos/${idProcesso}`, auth());
        addToast('success', 'Processo excluído.');
        onExcluido();
      } else {
        await axios.delete(`${API_BASE_URL}/processos/${idProcesso}/arquivos/${alvo.arquivo.id}`, auth());
        addToast('success', 'Arquivo excluído.');
        aposMudanca();
      }
    } catch (error) {
      addToast('error', mensagemDeErro(error, 'Não foi possível excluir'));
    }
  };

  const p = detalhe?.processo;

  return (
    <ModalOverlay>
      <ModalBox style={{ maxWidth: 820 }}>
        <ModalHeader>
          <h3><FaFolderOpen /> Processo {p ? `· ${p.placa || p.chassi}` : ''}</h3>
          <ModalClose onClick={onFechar}><FaTimes /></ModalClose>
        </ModalHeader>
        <ModalContent>
          {!p ? (
            <EmptyState>Carregando...</EmptyState>
          ) : editando ? (
            <FormularioProcesso processo={p} onCancelar={() => setEditando(false)} onSalvo={() => { setEditando(false); aposMudanca(); }} />
          ) : (
            <>
              <Resumo>
                <div><span>Data</span><strong>{formatarData(p.dataProcesso)}</strong></div>
                <div><span><FaCar /> Veículo</span><strong>{p.placa || '—'}</strong><em>{[p.marcaModelo, p.chassi].filter(Boolean).join(' · ')}</em></div>
                <div><span><FaUser /> Cliente</span><strong>{p.nomeCliente || '—'}</strong><em>{formatarDocumento(p.cpfCnpjCliente)}</em></div>
                <div><span>Serviços</span><strong>{p.servicos || '—'}</strong>{p.numeroOrdem && <em>da OS {p.numeroOrdem}</em>}</div>
                {p.observacao && <div className="inteiro"><span>Observação</span><p>{p.observacao}</p></div>}
              </Resumo>
              <AcoesTopo>
                <SecondaryButton type="button" onClick={() => setEditando(true)}><FaEdit /> Editar dados</SecondaryButton>
                {gestor && (
                  <BotaoPerigo type="button" onClick={() => setConfirmar({ tipo: 'processo' })}><FaTrash /> Excluir processo</BotaoPerigo>
                )}
              </AcoesTopo>

              <Secao>
                <h4>
                  <Bolinha $enviado={detalhe.arquivos.length > 0} />
                  Arquivos ({detalhe.arquivos.length})
                </h4>
                {detalhe.arquivos.length === 0 ? (
                  <EmptyState>Nenhum arquivo enviado ainda.</EmptyState>
                ) : (
                  <ListaArquivos>
                    {detalhe.arquivos.map((a) => (
                      <li key={a.id}>
                        <div>
                          <strong>{a.nomeOriginal}</strong>
                          <span>
                            {formatarTamanho(a.tamanho)} · {a.dataEnvio ? new Date(a.dataEnvio).toLocaleDateString('pt-BR') : ''}
                            {a.assinadoDigitalmente && <SeloAssinado><FaFileSignature /> Assinatura digital</SeloAssinado>}
                          </span>
                        </div>
                        <button type="button" onClick={() => baixar(a)} title="Baixar"><FaDownload /></button>
                        {gestor && (
                          <button type="button" className="perigo" onClick={() => setConfirmar({ tipo: 'arquivo', arquivo: a })} title="Excluir"><FaTrash /></button>
                        )}
                      </li>
                    ))}
                  </ListaArquivos>
                )}
              </Secao>

              <EnvioDeArquivos idProcesso={idProcesso} onEnviado={aposMudanca} />
            </>
          )}
        </ModalContent>

        <ConfirmModal
          isOpen={!!confirmar}
          onClose={() => setConfirmar(null)}
          onConfirm={excluir}
          title={confirmar?.tipo === 'processo' ? 'Excluir processo' : 'Excluir arquivo'}
          message={confirmar?.tipo === 'processo'
            ? 'O processo e todos os seus arquivos serão apagados definitivamente. Continuar?'
            : `O arquivo "${confirmar?.arquivo?.nomeOriginal}" será apagado definitivamente. Continuar?`}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      </ModalBox>
    </ModalOverlay>
  );
}

// ══════════════════════════════════════════════════════════════
// ENVIO: fila de arquivos (direto, assinado intacto, ou otimizar acima de 3 MB)
// ══════════════════════════════════════════════════════════════
let proximoId = 1;

function EnvioDeArquivos({ idProcesso, onEnviado }) {
  const { API_BASE_URL } = config;
  const [fila, setFila] = useState([]);
  const inputRef = useRef(null);

  const atualizar = (id, mudancas) => setFila((f) => f.map((i) => (i.id === id ? { ...i, ...mudancas } : i)));
  const remover = (id) => setFila((f) => {
    const item = f.find((i) => i.id === id);
    if (item?.resultado?.previaUrl) URL.revokeObjectURL(item.resultado.previaUrl);
    return f.filter((i) => i.id !== id);
  });

  const enviar = async (item, arquivos, assinado) => {
    atualizar(item.id, { estado: 'enviando', mensagem: 'Enviando...' });
    try {
      for (const arquivo of arquivos) {
        const corpo = new FormData();
        corpo.append('file', arquivo);
        corpo.append('assinado', assinado ? 'true' : 'false');
        await axios.post(`${API_BASE_URL}/processos/${idProcesso}/arquivos`, corpo, auth());
      }
      atualizar(item.id, { estado: 'enviado', mensagem: arquivos.length > 1 ? `Enviado em ${arquivos.length} partes` : 'Enviado' });
      onEnviado();
      setTimeout(() => remover(item.id), 2500);
    } catch (error) {
      atualizar(item.id, { estado: 'erro', mensagem: mensagemDeErro(error, 'Não foi possível enviar') });
    }
  };

  const escolher = async (e) => {
    const arquivos = Array.from(e.target.files || []);
    e.target.value = '';
    for (const arquivo of arquivos) {
      const id = proximoId++;
      let assinado = false;
      if (arquivo.type === 'application/pdf') {
        try { assinado = temAssinaturaDigital(new Uint8Array(await arquivo.arrayBuffer())); } catch (err) { assinado = false; }
      }
      const modo = classificarArquivo({ tipo: arquivo.type, tamanho: arquivo.size }, assinado);
      const item = { id, arquivo, assinadoDetectado: assinado, modo, estado: 'aguardando', mensagem: '', progresso: 0 };
      setFila((f) => [...f, item]);
      if (modo === 'direto') enviar(item, [arquivo], false);
      if (modo === 'assinado') enviar(item, [arquivo], true);
    }
  };

  const otimizar = async (item) => {
    atualizar(item.id, { estado: 'otimizando', progresso: 0, mensagem: 'Preparando...' });
    try {
      const resultado = await otimizarArquivo(item.arquivo, (progresso, mensagem) => atualizar(item.id, { progresso, mensagem }));
      atualizar(item.id, { estado: 'otimizado', resultado, mensagem: '' });
    } catch (error) {
      atualizar(item.id, { estado: 'erro', mensagem: error.message || 'Não foi possível otimizar este arquivo' });
    }
  };

  const abrirParte = (parte) => {
    const url = URL.createObjectURL(parte.arquivo);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <Secao>
      <h4><FaUpload /> Enviar arquivos</h4>
      <HintText style={{ marginBottom: 10 }}>
        PDF, JPG ou PNG. Até {LIMITE_COMUM_MB} MB por arquivo; acima disso o sistema oferece otimizar o tamanho. PDF com
        assinatura digital é reconhecido e enviado sem nenhuma alteração (até {LIMITE_ASSINADO_MB} MB).
      </HintText>
      <PrimaryButton type="button" onClick={() => inputRef.current && inputRef.current.click()}>
        <FaUpload /> Escolher arquivos
      </PrimaryButton>
      <input ref={inputRef} type="file" multiple accept="application/pdf,image/jpeg,image/png" onChange={escolher} style={{ display: 'none' }} />

      {fila.length > 0 && (
        <Fila>
          {fila.map((item) => (
            <ItemFila key={item.id} $estado={item.estado}>
              <div className="topo">
                <strong>{item.arquivo.name}</strong>
                <span>{formatarTamanho(item.arquivo.size)}</span>
                {item.assinadoDetectado && <SeloAssinado><FaFileSignature /> Assinatura digital detectada</SeloAssinado>}
                {['erro', 'aguardando', 'otimizado'].includes(item.estado) && (
                  <button type="button" className="fechar" onClick={() => remover(item.id)} title="Tirar da lista"><FaTimes /></button>
                )}
              </div>

              {item.modo === 'invalido' && <p className="erro">Tipo de arquivo não aceito (use PDF, JPG ou PNG).</p>}
              {item.modo === 'grandeDemais' && (
                <p className="erro">
                  Documento com assinatura digital acima de {LIMITE_ASSINADO_MB} MB: não pode ser otimizado (perderia a
                  assinatura). Peça ao emissor o documento em partes.
                </p>
              )}

              {item.modo === 'otimizar' && item.estado === 'aguardando' && (
                <div className="pergunta">
                  <p>
                    Este arquivo tem <strong>{formatarTamanho(item.arquivo.size)}</strong> (limite de {LIMITE_COMUM_MB} MB).
                    Deseja <strong>otimizar o tamanho</strong>? A qualidade é reduzida para leitura e você confere antes de enviar.
                  </p>
                  <div className="botoes">
                    <PrimaryButton type="button" onClick={() => otimizar(item)}><FaCompressAlt /> Otimizar tamanho</PrimaryButton>
                    {item.arquivo.type === 'application/pdf' && item.arquivo.size <= LIMITE_ASSINADO_MB * 1024 * 1024 && (
                      <SecondaryButton type="button" onClick={() => enviar(item, [item.arquivo], true)} title="O sistema confere a assinatura antes de aceitar">
                        <FaFileSignature /> É assinado digitalmente: enviar sem alterar
                      </SecondaryButton>
                    )}
                  </div>
                </div>
              )}

              {item.estado === 'otimizando' && (
                <>
                  <Barra><div style={{ width: `${item.progresso}%` }} /></Barra>
                  <p className="info">{item.mensagem}</p>
                </>
              )}

              {item.estado === 'otimizado' && item.resultado && (
                <div className="resultado">
                  <img src={item.resultado.previaUrl} alt="Prévia da primeira página otimizada" />
                  <div>
                    <p>
                      <strong>{formatarTamanho(item.resultado.tamanhoOriginal)} → {formatarTamanho(item.resultado.partes.reduce((s, p) => s + p.tamanho, 0))}</strong>
                      {' '}({item.resultado.totalPaginas} página(s), otimização {item.resultado.nivel})
                    </p>
                    {item.resultado.partes.length > 1 && (
                      <p className="aviso">
                        Mesmo otimizado não coube em {LIMITE_COMUM_MB} MB: será enviado em {item.resultado.partes.length} partes,
                        na ordem das páginas.
                      </p>
                    )}
                    <ul>
                      {item.resultado.partes.map((parte) => (
                        <li key={parte.arquivo.name}>
                          <button type="button" className="link" onClick={() => abrirParte(parte)}>{parte.arquivo.name}</button>
                          {' '}· {formatarTamanho(parte.tamanho)} · {parte.paginas} pág.
                        </li>
                      ))}
                    </ul>
                    <p className="info">Confira se o texto ficou legível (abra o PDF acima) antes de enviar.</p>
                    <div className="botoes">
                      <PrimaryButton type="button" onClick={() => enviar(item, item.resultado.partes.map((p) => p.arquivo), false)}>
                        <FaUpload /> Enviar otimizado
                      </PrimaryButton>
                      <SecondaryButton type="button" onClick={() => remover(item.id)}>Descartar</SecondaryButton>
                    </div>
                  </div>
                </div>
              )}

              {item.estado === 'enviando' && <p className="info">{item.mensagem}</p>}
              {item.estado === 'enviado' && <p className="ok"><FaCheckCircle /> {item.mensagem}</p>}
              {item.estado === 'erro' && <p className="erro">{item.mensagem}</p>}
            </ItemFila>
          ))}
        </Fila>
      )}
    </Secao>
  );
}

// ══════════════════════════════════════════════════════════════
// ESTILOS — paleta Nexus (#004d4d / #008080 / #20b2aa)
// ══════════════════════════════════════════════════════════════
const FiltrosForm = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  align-items: end;
  label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 600; color: #34495e; }
  .largo { grid-column: span 2; }
  .botoes { display: flex; gap: 8px; }
  @media (max-width: 600px) { .largo { grid-column: span 1; } }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LinhaProcesso = styled.button`
  display: grid;
  grid-template-columns: 14px 86px 1.1fr 1.3fr 1.6fr auto;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  font: inherit;
  background: white;
  border: 1px solid #e3e8e8;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover { border-color: #008080; box-shadow: 0 3px 10px rgba(0,128,128,0.12); }
  strong { display: block; color: #004d4d; font-size: 13.5px; }
  span { display: block; color: #7f8c8d; font-size: 12px; }
  .data { color: #34495e; font-size: 13px; font-variant-numeric: tabular-nums; }
  .servicos { color: #34495e; font-size: 12.5px; }
  .servicos em { color: #b0b8bc; }
  .extra { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
  .os { color: #008080; font-weight: 700; }
  @media (max-width: 800px) {
    grid-template-columns: 14px 1fr;
    .data, .cliente, .servicos, .extra { grid-column: 2; }
  }
`;

const Bolinha = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.$enviado ? '#27ae60' : '#e74c3c')};
  box-shadow: 0 0 0 3px ${(p) => (p.$enviado ? 'rgba(39,174,96,0.18)' : 'rgba(231,76,60,0.18)')};
`;

const Paginacao = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  font-size: 13px;
  color: #5f6f6f;
  label { display: flex; align-items: center; gap: 8px; }
  select { padding: 6px 8px; }
  div { display: flex; gap: 6px; }
`;

const BotaoPagina = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: #eaf6f6;
  color: #008080;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: default; }
`;

// Barra abaixo dos filtros: atalhos à esquerda, contador da página à direita
const BarraExibicao = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef1f1;

  .atalhos { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .dia { display: inline-flex; align-items: center; gap: 6px; color: #004d4d; font-size: 13.5px; }
  .legenda { color: #7f8c8d; font-size: 12.5px; }
  .contadores { display: flex; gap: 10px; flex-wrap: wrap; margin-left: auto; }
`;

const Atalho = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid rgba(0,128,128,0.35);
  border-radius: 8px;
  background: white;
  color: #006666;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &.ativo { background: #008080; border-color: #008080; color: white; }
`;

// Mesmo modelo dos contadores da tela de OS: número grande, rótulo pequeno, borda na cor do status
const Contador = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  min-width: 90px;
  background: white;
  border: 1.5px solid ${(p) => p.$cor || '#3498db'};
  border-radius: 8px;

  strong { font-size: 18px; font-weight: 700; color: ${(p) => p.$cor || '#2c3e50'}; }
  span { font-size: 10px; color: #7f8c8d; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; text-align: center; }
`;

const Abas = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  border-bottom: 2px solid #eef1f1;
  button {
    display: flex; align-items: center; gap: 6px;
    padding: 9px 14px;
    border: none; background: none; cursor: pointer;
    font-weight: 600; color: #7f8c8d;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
  }
  .ativa { color: #004d4d; border-bottom-color: #008080; }
`;

const ListaOrdens = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 50vh;
  overflow-y: auto;
  .ordem { border: 1px solid #e3e8e8; border-radius: 10px; padding: 10px 12px; }
  .cabecalho { display: flex; gap: 10px; align-items: baseline; margin-bottom: 6px; }
  .cabecalho strong { color: #004d4d; }
  .cabecalho span { color: #7f8c8d; font-size: 12.5px; }
  label { display: flex; align-items: center; gap: 8px; padding: 6px 4px; font-size: 13px; cursor: pointer; color: #34495e; }
  label em { margin-left: auto; color: #95a5a6; font-size: 12px; }
  label.feito { opacity: 0.55; cursor: default; }
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  align-items: end;
  label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 600; color: #34495e; }
  .largo { grid-column: span 2; }
  .inteiro { grid-column: 1 / -1; }
  .acao { display: flex; }
  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
    .largo { grid-column: 1 / -1; }
  }
`;

// Resultados da lupa do cliente e veículos do cadastro dele
const ListaEscolha = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e3e8e8;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  button {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px;
    border: none; border-bottom: 1px solid #f0f3f3;
    background: white; cursor: pointer; text-align: left;
    font-size: 13px; color: #34495e;
  }
  button:last-child { border-bottom: none; }
  button:hover { background: #f0fdfa; }
  button.escolhido { background: #f6fff8; }
  svg { color: #008080; flex-shrink: 0; }
  span { color: #7f8c8d; font-size: 12px; }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  button {
    padding: 3px 9px;
    border: 1px solid rgba(0,128,128,0.3);
    border-radius: 12px;
    background: #f0fdfa;
    color: #006666;
    font-size: 11.5px;
    cursor: pointer;
  }
`;

const Rodape = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Resumo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  div { display: flex; flex-direction: column; gap: 2px; }
  span { font-size: 11.5px; color: #7f8c8d; display: flex; align-items: center; gap: 5px; }
  strong { color: #004d4d; font-size: 14px; word-break: break-word; }
  em { color: #7f8c8d; font-size: 12px; font-style: normal; word-break: break-word; }
  p { margin: 0; color: #34495e; font-size: 13px; white-space: pre-wrap; }
  .inteiro { grid-column: 1 / -1; }
`;

const AcoesTopo = styled.div`
  display: flex;
  gap: 8px;
  margin: 14px 0 6px 0;
  flex-wrap: wrap;
`;

const BotaoPerigo = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: 1px solid #f5c6cb;
  border-radius: 10px;
  background: #fff5f5;
  color: #c0392b;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
`;

const Secao = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 2px solid #eef1f1;
  h4 { display: flex; align-items: center; gap: 8px; margin: 0 0 10px 0; color: #004d4d; }
`;

const ListaArquivos = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  li { display: flex; align-items: center; gap: 8px; padding: 9px 12px; border: 1px solid #eef1f1; border-radius: 8px; }
  li div { flex: 1; min-width: 0; }
  li strong { display: block; font-size: 13.5px; color: #2c3e50; word-break: break-all; }
  li span { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 12px; color: #7f8c8d; }
  button { width: 34px; height: 34px; border: none; border-radius: 8px; background: #eaf6f6; color: #008080; cursor: pointer; }
  button.perigo { background: #fff5f5; color: #c0392b; }
`;

const SeloAssinado = styled.span`
  display: inline-flex !important;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #ebf5fb;
  color: #2471a3 !important;
  font-size: 11px !important;
  font-weight: 700;
`;

const Fila = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const ItemFila = styled.div`
  border: 1px solid ${(p) => (p.$estado === 'erro' ? '#f5c6cb' : p.$estado === 'enviado' ? '#c3e6cb' : '#e3e8e8')};
  background: ${(p) => (p.$estado === 'erro' ? '#fffafa' : p.$estado === 'enviado' ? '#f6fff8' : 'white')};
  border-radius: 10px;
  padding: 10px 12px;
  .topo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .topo strong { font-size: 13.5px; color: #2c3e50; word-break: break-all; }
  .topo > span { font-size: 12px; color: #7f8c8d; }
  .fechar { margin-left: auto; border: none; background: none; color: #95a5a6; cursor: pointer; }
  p { margin: 8px 0 0 0; font-size: 13px; color: #34495e; }
  .erro { color: #c0392b; }
  .ok { color: #1e8449; display: flex; align-items: center; gap: 6px; }
  .info { color: #7f8c8d; font-size: 12.5px; }
  .aviso { color: #b9770e; }
  .botoes { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
  .resultado { display: flex; gap: 14px; margin-top: 10px; align-items: flex-start; }
  .resultado img { width: 140px; border: 1px solid #ddd; border-radius: 6px; flex-shrink: 0; }
  .resultado ul { margin: 6px 0 0 0; padding-left: 18px; font-size: 12.5px; color: #5f6f6f; }
  .link { border: none; background: none; padding: 0; color: #2471a3; text-decoration: underline; cursor: pointer; font-size: 12.5px; }
  @media (max-width: 600px) { .resultado { flex-direction: column; } }
`;

const Barra = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #e3e6ea;
  overflow: hidden;
  margin-top: 10px;
  div { height: 100%; background: #008080; transition: width 0.3s ease; }
`;

export default Processos;
